import React from 'react';
import { Question } from '../types';
import { useDisplayTranslation } from '../hooks/useDisplayTranslation';

interface FlashcardProps {
    question: Question;
    isFlipped: boolean;
    onFlip: () => void;
    displayLanguage: 'pt' | 'en' | 'es';
}

const Flashcard: React.FC<FlashcardProps> = ({ question, isFlipped, onFlip, displayLanguage }) => {
    const { stem, options: displayOptions, explanation: locExpl, loading: trLoading } = useDisplayTranslation(question, displayLanguage);

    const getOptionClass = (optionKey: string) => {
        const isCorrect = question.correctAnswers.includes(optionKey);
        return isCorrect ? 'bg-green-100 dark:bg-green-800/60 border-green-300 dark:border-green-500' : 'bg-gray-100 dark:bg-slate-700 border-gray-200 dark:border-slate-600';
    };

    return (
        <div className={`flashcard-container w-full h-[500px] max-h-[80vh] min-h-[400px] cursor-pointer ${isFlipped ? 'flipped' : ''}`} onClick={onFlip}>
            <div className="flashcard-inner relative">
                {trLoading && displayLanguage !== 'pt' && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 dark:bg-slate-900/80">
                        <span className="text-sm text-gray-600 dark:text-slate-300">
                            {displayLanguage === 'en' ? 'Translating…' : displayLanguage === 'es' ? 'Traduciendo…' : '…'}
                        </span>
                    </div>
                )}
                <div className="flashcard-front bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-lg shadow-2xl justify-center items-center text-center">
                    <div
                        className="prose prose-lg dark:prose-invert text-gray-700 dark:text-slate-300 mb-6"
                        dangerouslySetInnerHTML={{ __html: stem }}
                    />
                    <div className="text-center text-gray-500 dark:text-slate-400 mt-8 text-sm absolute bottom-6 left-1/2 -translate-x-1/2">
                        <i className="fas fa-mouse-pointer-alt mr-2"></i>
                        {displayLanguage === 'en'
                            ? 'Click to reveal the answer'
                            : displayLanguage === 'es'
                              ? 'Haz clic para revelar la respuesta'
                              : 'Clique para revelar a resposta'}
                    </div>
                </div>

                <div className="flashcard-back bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-lg shadow-2xl">
                    <div
                        className="prose prose-lg dark:prose-invert text-gray-700 dark:text-slate-300 mb-4 border-b border-gray-200 dark:border-slate-700 pb-4"
                        dangerouslySetInnerHTML={{ __html: stem }}
                    />
                    
                    <h3 className="text-lg font-bold text-orange-400 mt-4 mb-3">
                        {(displayLanguage === 'en'
                            ? 'Correct answer(s)'
                            : displayLanguage === 'es'
                              ? 'Respuesta(s) correcta(s)'
                              : 'Resposta(s) Correta(s)') + ': '}
                        {question.correctAnswers.join(', ')}
                    </h3>
                    <div className="space-y-2 mb-6">
                        {Object.entries(displayOptions).map(([key, value]) => (
                            <div
                                key={key}
                                className={`p-3 border rounded-lg transition-all text-sm ${getOptionClass(key)}`}
                            >
                                <div className="flex items-center">
                                    <span className="font-bold mr-2 text-orange-400">{key}.</span>
                                    <span dangerouslySetInnerHTML={{ __html: value }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-200 dark:border-slate-700">
                        <h3 className="text-lg font-bold text-orange-400 mb-2">
                            {displayLanguage === 'en' ? 'Explanation' : displayLanguage === 'es' ? 'Explicación' : 'Explicação'}
                        </h3>
                        <p className="text-gray-700 dark:text-slate-300 text-sm">{locExpl.general}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Flashcard;
